import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { createReferenceStringWithHistory } from '@seen/core/references'
import { isSameDay } from 'date-fns'
import { sortBy } from 'lodash-es'
import type { ReactNode } from 'react'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import { RichText } from '#src/components/RichText.tsx'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'

export function useObservationActivity(
  observation: ExtendedObservation
): ReactNode[] {
  const { data: pastObservations = [] } = useResourceVersions(
    ExtendedObservation,
    observation.id
  )

  const ascendingObservations = sortBy(pastObservations, (a) => a.lastUpdated)
  const observationOriginOf = useVersionOriginTask(pastObservations)

  const ascendingActivity: {
    observation: ExtendedObservation
    node: ReactNode
  }[] = []
  ascendingObservations.forEach((o, idx) => {
    if (idx === 0) {
      ascendingActivity.push({
        observation: o,
        node: (
          <ActivityEntry
            key={createReferenceStringWithHistory(o.toFhirResource())}
            author={o.lastUpdatedBy}
            time={o.lastUpdated}
            action="created"
            originTask={observationOriginOf(o)}
          >
            <RichText value={o.valueString ?? ''} />
          </ActivityEntry>
        ),
      })
      return
    }

    const prevObservation = ascendingActivity.at(-1)?.observation
    invariant(
      prevObservation,
      'Previous observation must exist when processing observation history'
    )

    if (prevObservation.valueString === o.valueString) {
      return
    }

    const entry = {
      observation: o,
      node: (
        <ActivityEntry
          key={createReferenceStringWithHistory(o.toFhirResource())}
          author={o.lastUpdatedBy}
          time={o.lastUpdated}
          action="edited"
          originTask={observationOriginOf(o)}
        >
          <RichText value={o.valueString ?? ''} />
        </ActivityEntry>
      ),
    }

    if (prevObservation.lastUpdatedBy.reference !== o.lastUpdatedBy.reference) {
      ascendingActivity.push(entry)
      return
    }

    // We only want to show one edit per day, so we add an entry if we've moved to a different day or replace the last edit otherwise.
    if (!isSameDay(prevObservation.lastUpdated, o.lastUpdated)) {
      ascendingActivity.push(entry)
    } else {
      ascendingActivity[ascendingActivity.length - 1] = entry
    }
  })

  const activity = ascendingActivity.reverse().map(({ node }) => node)
  return activity
}
